/**
 * Campaign delivery attribution — the portal half of
 * `campaign_delivery_attribution_v1`.
 *
 * The backend publishes, beside `dailyByChannel`, the SAME delivery named by
 * the campaign it was attributed to and classified against that campaign's
 * own approved flight (`canonicalSummary.campaignDelivery`). This module is
 * the one reader of that contract for both portals: the tooltip rows that let
 * a day's dollars be reconciled by campaign, platform, spend and delivery
 * status; the restrained "delivery outside approved flight" notice; and the
 * welcome tile that counts campaigns by CONFIRMED delivery rather than by
 * approved-flight membership.
 *
 * Byte-identical in Cortex and Quantum (pinned by crossRepositoryParity):
 * the two portals must attribute the same day to the same campaigns, with
 * the same words. Nothing here derives a number of its own — every figure is
 * read off the contract, already client-billed.
 */

const isIsoDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

const DELIVERY_STATUS_LABELS = Object.freeze({
  in_flight: "",
  late_arriving_in_flight: "late-arriving",
  outside_approved_flight: "outside approved flight",
  before_approved_flight: "before approved flight",
  flight_not_established: "flight not established",
  unattributed: "unattributed",
});

export const DELIVERY_OUTSIDE_APPROVED_FLIGHT_TITLE = "Delivery outside approved flight";
// The tile is one KPI to the reader — "Campaigns Live" in every state. The two
// identifiers survive because the BASIS behind the number still differs: the
// first is confirmed delivery at the reporting cutoff, the second the
// approved-in-flight count a pre-contract cached payload can still supply.
// The evidence state is carried by the value and the meta line, never by a
// changing tile name.
export const CAMPAIGNS_DELIVERING_LABEL = "Campaigns Live";
export const CAMPAIGNS_RUNNING_LABEL = "Campaigns Live";
export const CAMPAIGNS_RUNNING_META = "In-flight approved campaigns today";

const defaultFormatDate = (value) => {
  if (!isIsoDay(value)) return String(value || "");
  const date = new Date(`${value}T00:00:00Z`);
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(date);
};

const defaultFormatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: numeric < 100 ? 2 : 0,
    maximumFractionDigits: numeric < 100 ? 2 : 0,
  }).format(numeric);
};

const toFinite = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * The contract, wherever the payload nests it: on the canonical summary
 * itself, or one envelope down (the welcome payload wraps the summary).
 */
export function resolveCampaignDelivery(source) {
  if (!source || typeof source !== "object") return null;
  const direct = source.campaignDelivery;
  if (direct && typeof direct === "object" && Array.isArray(direct.days)) return direct;
  const nested =
    source.canonicalSummary?.campaignDelivery ||
    source.canonicalReporting?.campaignDelivery ||
    null;
  return nested && Array.isArray(nested.days) ? nested : null;
}

const statusLabel = (status) => DELIVERY_STATUS_LABELS[String(status || "")] ?? String(status || "");

/*
 * Campaign keys sort numerically; the unattributed bucket (never a campaign)
 * always comes last so a reader meets the named campaigns first.
 */
const compareCampaignKeys = (left, right) => {
  const a = Number(left);
  const b = Number(right);
  const aNumeric = Number.isInteger(a);
  const bNumeric = Number.isInteger(b);
  if (aNumeric && bNumeric) return a - b;
  if (aNumeric) return -1;
  if (bNumeric) return 1;
  return String(left).localeCompare(String(right));
};

/**
 * Whether a day's tooltip should name campaigns at all. Attribution rows
 * exist to RECONCILE: they are added when more than one campaign shares the
 * scope, or when any delivery is classified as anything other than in-flight,
 * or when some delivery could not be attributed. A single in-flight campaign
 * has nothing to reconcile — its platform rows already say everything.
 */
export function campaignAttributionNeeded(campaignDelivery) {
  const contract = resolveCampaignDelivery({ campaignDelivery });
  if (!contract) return false;
  const campaigns = Array.isArray(contract.campaigns) ? contract.campaigns : [];
  if (campaigns.length > 1) return true;
  if (contract.unattributed?.present) return true;
  return campaigns.some((entry) => entry.deliveryStatus && entry.deliveryStatus !== "in_flight");
}

/**
 * Tooltip rows for one reporting day: one per campaign × platform, in the
 * chart's own `extraRows` shape ({ label, value }), spend already
 * client-billed. The status rides the LABEL so the value column stays a
 * number.
 */
export function buildCampaignDeliveryTooltipRows({
  campaignDelivery,
  date,
  resolvePlatformLabel = (key) => key,
  formatCurrency = defaultFormatCurrency,
} = {}) {
  const contract = resolveCampaignDelivery({ campaignDelivery });
  if (!contract || !campaignAttributionNeeded(contract)) return [];
  const day = (contract.days || []).find((entry) => String(entry?.date) === String(date));
  if (!day || !day.byCampaign) return [];

  const rows = [];
  Object.keys(day.byCampaign)
    .sort(compareCampaignKeys)
    .forEach((campaignKey) => {
      const entry = day.byCampaign[campaignKey] || {};
      const campaignName = entry.campaignName || (entry.campaignId ? `Campaign ${entry.campaignId}` : "Unattributed");
      const status = statusLabel(entry.deliveryStatus);
      Object.keys(entry.byChannel || {})
        .sort()
        .forEach((channel) => {
          const metrics = entry.byChannel[channel] || {};
          const spend = toFinite(metrics.spend ?? metrics.clientFacingSpend);
          if (spend == null) return;
          const platform = resolvePlatformLabel(channel) || channel;
          rows.push({
            label: [campaignName, platform, status].filter(Boolean).join(" · "),
            value: formatCurrency(spend),
            campaignId: entry.campaignId ?? null,
            platformKey: channel,
            deliveryStatus: entry.deliveryStatus || null,
          });
        });
    });
  return rows;
}

/**
 * The post-flight notice: null when nothing delivered outside an approved
 * flight. One line per campaign — its name, the approved end it delivered
 * past, the days observed, the platforms and the client-billed spend — so the
 * reader can act on the fact rather than discover it as a discrepancy.
 */
export function resolvePostFlightDeliveryNotice({
  campaignDelivery,
  formatDate = defaultFormatDate,
  formatCurrency = defaultFormatCurrency,
  resolvePlatformLabel = (key) => key,
} = {}) {
  const contract = resolveCampaignDelivery({ campaignDelivery });
  if (!contract) return null;
  const outside = (contract.campaigns || []).filter(
    (entry) => Array.isArray(entry?.outOfFlight?.dates) && entry.outOfFlight.dates.length > 0
  );
  if (!outside.length) return null;

  const campaigns = outside.map((entry) => {
    const dates = entry.outOfFlight.dates.filter(isIsoDay);
    const first = dates[0];
    const last = dates[dates.length - 1];
    const observed = !first ? "" : first === last ? formatDate(first) : `${formatDate(first)}–${formatDate(last)}`;
    const approvedEnd =
      entry.outOfFlight.approvedFlightEnd ||
      entry.approvedFlight?.authorizedDeliveryEnd ||
      entry.approvedFlight?.end ||
      null;
    const platforms = (entry.outOfFlight.platforms || []).map((key) => resolvePlatformLabel(key) || key);
    const spend = toFinite(entry.outOfFlight.spend);
    return {
      campaignId: entry.campaignId ?? null,
      campaignName: entry.campaignName || (entry.campaignId ? `Campaign ${entry.campaignId}` : "Campaign"),
      approvedFlightEnd: approvedEnd,
      observedDates: dates,
      platforms: entry.outOfFlight.platforms || [],
      spend,
      detail: [
        entry.campaignName || (entry.campaignId ? `Campaign ${entry.campaignId}` : "Campaign"),
        approvedEnd ? `approved flight ended ${formatDate(approvedEnd)}` : null,
        observed ? `delivery observed ${observed}` : null,
        platforms.length ? platforms.join(", ") : null,
        spend != null ? formatCurrency(spend) : null,
      ]
        .filter(Boolean)
        .join(" · "),
    };
  });

  return {
    title: DELIVERY_OUTSIDE_APPROVED_FLIGHT_TITLE,
    campaigns,
    detail: campaigns.map((entry) => entry.detail).join(" — "),
  };
}

/**
 * The welcome tile: campaigns with CONFIRMED delivery at the latest complete
 * reporting cutoff. Reads the backend's evidence-based headline
 * (`metrics.campaignsDelivering` + `metrics.deliveryScope`); a payload from
 * before the contract existed still renders as the approved-in-flight count
 * under its old name, so a cached page never shows an empty tile.
 */
export function buildCampaignsDeliveringTileModel(summary, { formatDate = defaultFormatDate } = {}) {
  const metrics = summary?.metrics || {};
  const scope = metrics.deliveryScope && typeof metrics.deliveryScope === "object" ? metrics.deliveryScope : null;
  const delivering = toFinite(metrics.campaignsDelivering);
  const running = toFinite(metrics.campaignsRunning);

  if (scope && scope.state === "OBSERVED" && delivering != null) {
    const approvedInFlight = toFinite(scope.approvedInFlightCount) ?? running ?? 0;
    /*
     * THE VALUE IS CURRENT STATE, NOT YESTERDAY'S DELIVERY.
     *
     * This tile is labelled "Campaigns Live". It used to render
     * `metrics.campaignsDelivering` — campaigns with confirmed delivery at the
     * latest COMPLETE reporting cutoff, which is by definition a day that has
     * already ended. While something is in flight the two agree and nobody
     * notices. The morning after an advertiser's last flight closes they part
     * company, and the tile read "Campaigns Live 1" directly above its own
     * supporting line "0 approved in-flight", beside "Platforms Live 0" and a
     * campaign card reading DONE.
     *
     * The value now comes from the same current-state authority the supporting
     * copy already names. `metrics.campaignsRunning` and
     * `deliveryScope.approvedInFlightCount` are not two opinions: the welcome
     * route derives both from the one `runningCampaigns` set (campaigns inside
     * an approved flight today, internal-authorization rows excluded), so the
     * headline and the line beneath it cannot disagree.
     *
     * `campaignsDelivering` is untouched and still published — it is a real
     * fact and the reporting diagnostics read it. It is simply not what "live"
     * means, and it is never a fallback here: an absent or malformed current
     * count reads 0, exactly as `platformsLive` does, because "we cannot tell"
     * must not be printed as "still running".
     */
    /* `running` is already normalized; re-wrapping it would turn an absent
       count into 0 (Number(null) === 0) and hide the scope fallback. */
    const liveNow = running ?? toFinite(scope.approvedInFlightCount);
    const outside = Array.isArray(scope.deliveringOutsideApprovedFlight)
      ? scope.deliveringOutsideApprovedFlight.filter((entry) => entry?.deliveringAtCutoff !== false).length
      : 0;
    const cutoff = isIsoDay(scope.latestCompleteDeliveryDate) ? formatDate(scope.latestCompleteDeliveryDate) : null;
    const parts = [`${approvedInFlight} approved in-flight`];
    if (outside > 0) parts.push(`${outside} delivering outside flight`);
    else if (cutoff) parts.push(`confirmed delivery through ${cutoff}`);
    return {
      basis: "observed_delivery",
      label: CAMPAIGNS_DELIVERING_LABEL,
      value: liveNow != null ? Math.max(0, Math.trunc(liveNow)) : 0,
      meta: parts.join(" · "),
      cutoffDate: scope.latestCompleteDeliveryDate || null,
      approvedInFlightCount: approvedInFlight,
      deliveringOutsideFlightCount: outside,
    };
  }

  if (scope && scope.state === "DELIVERY_EVIDENCE_UNAVAILABLE") {
    return {
      basis: "evidence_unavailable",
      label: CAMPAIGNS_DELIVERING_LABEL,
      value: null,
      meta: `Delivery evidence unavailable · ${toFinite(scope.approvedInFlightCount) ?? running ?? 0} approved in-flight`,
      cutoffDate: null,
      approvedInFlightCount: toFinite(scope.approvedInFlightCount) ?? running ?? 0,
      deliveringOutsideFlightCount: 0,
    };
  }

  return {
    basis: "approved_in_flight",
    label: CAMPAIGNS_RUNNING_LABEL,
    value: running != null ? Math.max(0, Math.trunc(running)) : 0,
    meta: CAMPAIGNS_RUNNING_META,
    cutoffDate: null,
    approvedInFlightCount: running ?? 0,
    deliveringOutsideFlightCount: 0,
  };
}
